import axios from 'axios'
import { GetDefaultUrl } from '@/utils/ConfigHelper';

const LoadBase64FromFile = file => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = function () {
            resolve(reader.result)
        }
        reader.onerror = function () {
            reject(reader.error)
        }
        reader.readAsDataURL(file)
    })
}

const ArrayBufferToBase64 = buffer => {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    const len = bytes.byteLength
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
}

const DataUrlToFile = async (url, type, ext) => {
    const { data } = await axios.get(url, { responseType: 'blob' })
    return new File([data], `${Date.now()}.${ext}`, { type })
}

const BlobToFile = (blob, fileName) => {
    return new File([blob], fileName, {
        type: blob.type,
        lastModified: Date.now()
    });
}

const Base64ToBlob = (base64, contentType = "", sliceSize = 512) => {
    const byteCharacters = atob(base64.split(",")[1]);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length;
        offset += sliceSize) {
        const slice = byteCharacters.slice(
            offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
}

const GetImageObject = url => {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => {
            resolve(image)
        }
        image.onerror = e => {
            reject(e)
        }
        image.src = url
    })
}

const GetImageSize = url => {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.onload = () => {
            resolve({
                width: image.naturalWidth,
                height: image.naturalHeight,
            })
        }
        image.onerror = e => {
            reject(e)
        }
        image.src = url
    })
}

const CalculateImageSize = (width, height, maxSize = 2048) => {
    if (width <= maxSize && height <= maxSize) return { width, height };

    const ratio = width / height;
    if (ratio > 1) return { width: maxSize, height: maxSize / ratio };
    else return { width: maxSize * ratio, height: maxSize };
}

const ResizeImage = async (src, width, height, ext = 'png') => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const image = await GetImageObject(src)
    canvas.width = width
    canvas.height = height

    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, width, height)
    return {
        src: canvas.toDataURL(`image/${ext}`),
        ext: ext
    }
}

const ResizeImageToFile = async (src, width, height, ext = 'png') => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const image = await GetImageObject(src)
    canvas.width = width
    canvas.height = height

    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, 0, width, height);

    return new Promise((resolve) => {
        canvas.toBlob(async (blob) => {
            const file = new File([blob], `image.${ext}`, { type: `image/${ext}` });
            resolve({
                file,
                ext
            });
        }, `image/${ext}`);
    });
}

const GetBase64FromURLWithMetadata = async (url) => {
    const response = await axios.get(url, { responseType: 'arraybuffer' });

    const type = response.headers['content-type'];
    const ext = type.split('/')[1];

    const arrayBuffer = response.data;
    const encoded = ArrayBufferToBase64(arrayBuffer);
    const base64 = `data:${type};base64,${encoded}`;
    const file = await DataUrlToFile(base64, type, ext);

    var sizeInBytes = Math.ceil((encoded.length / 4) * 3);
    const lastStr = encoded[encoded.length - 1];
    if (lastStr === '==') sizeInBytes -= 2;
    else sizeInBytes -= 1;

    return {
        base64,
        size: sizeInBytes,
        ext,
    }
}

const GetBase64FromGettyImagesURLWithMetadata = async (token, id) => {
    // TO-DO : 로그 삭제
    // console.log(`GetBase64FromGettyImagesURLWithMetadata : ${id}`, 'jv13814161')
    // id = 'jv13814161'

    const url = `${GetDefaultUrl()}/apis/provider/gettyimages/images/${id}/direct-download`;

    const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'arraybuffer' });

    const type = response.headers['content-type'];
    const ext = type.split('/')[1];

    const arrayBuffer = response.data;
    const encoded = ArrayBufferToBase64(arrayBuffer);
    const base64 = `data:${type};base64,${encoded}`;
    const file = await DataUrlToFile(base64, type, ext);

    var sizeInBytes = Math.ceil((encoded.length / 4) * 3);
    const lastStr = encoded[encoded.length - 1];
    if (lastStr === '==') sizeInBytes -= 2;
    else sizeInBytes -= 1;

    return {
        base64,
        size: sizeInBytes,
        ext,
    }
}

const GetThumbnailFromVideo = async (videoFile) => {
    // 비디오 요소 생성
    return new Promise((resolve, reject) => {
        try {
            const video = document.createElement('video');
            video.src = URL.createObjectURL(videoFile);
            video.crossOrigin = 'anonymous';
        
            // 캔버스 요소 생성
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
        
            video.addEventListener('canplay', () => {
                if (video.currentTime === 0) video.currentTime = 1; // 1초로 이동
            });

            video.addEventListener('seeked', () => {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnailUrl = canvas.toDataURL('image/png');
                resolve(thumbnailUrl);
                URL.revokeObjectURL(video.src);
            });

            video.addEventListener('error', (e) => {
                reject(e);
            });
        
            video.load();
        }
        catch (e) {
            reject(e);
        }
    })
}

export default {
    LoadBase64FromFile,
    ArrayBufferToBase64,
    DataUrlToFile,
    BlobToFile,
    Base64ToBlob,
    GetImageObject,
    GetImageSize,
    CalculateImageSize,
    ResizeImage,
    ResizeImageToFile,
    GetBase64FromURLWithMetadata,
    GetBase64FromGettyImagesURLWithMetadata,
    GetThumbnailFromVideo,
}